import { memo, useCallback, useState } from 'react';
import { Button } from '../../components/Button';
import { LedgerRow } from '../../components/CreditLedger/LedgerRow';
import { Modal } from '../../components/Modal';
import { useDownloadInvoice } from '../../hooks/useDownloadInvoice';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { T, useT } from '../../translation/src';
import { RefreshInvoicesButton } from './RefreshInvoicesButton';
import { useSuperadminCreateOdooInvoiceMutation, useSuperadminCreditLedgerQuery } from '../../types/graphqlTypes';

const PAGE_SIZE = 50;

export interface HistoryModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  organisationName: string;
}
// The superadmin's view of an organisation's purchase history: every grant and fee with its expiry, its
// draft invoice in Odoo (or the button to make a missing one), and the sent invoice to download.
const HistoryModal_: React.FC2<HistoryModalProps> = ({ onClose, open, organisationId, organisationName }) => {
  const { showToast } = useToastNotifications();
  const invoiceFailed = useT('could not make the draft invoice', { swc: true });
  // The mutation returns the row with its draft: the cache updates the row by itself.
  const [createInvoice] = useSuperadminCreateOdooInvoiceMutation();
  const [creatingEntryId, setCreatingEntryId] = useState<string | null>(null);
  const onCreateInvoice = useCallback(
    async (entryId: string) => {
      setCreatingEntryId(entryId);
      try {
        await createInvoice({ variables: { entryId } });
      } catch (e) {
        showToast({ title: invoiceFailed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      } finally {
        setCreatingEntryId(null);
      }
    },
    [createInvoice, invoiceFailed, showToast],
  );
  const { data, fetchMore, loading, refetch } = useSuperadminCreditLedgerQuery({
    variables: { organisationId, pagination: { limit: PAGE_SIZE } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });
  // The same download the customer has in their purchase history, once sales has sent the invoice.
  const { downloadInvoice, downloadingEntryId } = useDownloadInvoice({ organisationId });

  const page = data?.superadminCreditLedger;
  const entries = page?.data || [];
  const nextCursor = page?.nextCursor;
  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { organisationId, pagination: { limit: PAGE_SIZE, cursor: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        superadminCreditLedger: { ...fetchMoreResult.superadminCreditLedger, data: [...previous.superadminCreditLedger.data, ...fetchMoreResult.superadminCreditLedger.data] },
      }),
    });
  }, [fetchMore, nextCursor, organisationId]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2 min-w-0">
          <span className="truncate">
            <T _str="purchase history" swc /> - {organisationName}
          </span>
          <RefreshInvoicesButton organisationId={organisationId} onRefreshed={refetch} />
        </span>
      </Modal.Header>
      <Modal.Body className="px-1">
        {/* Room for the scrollbar, so prices are not under it. */}
        <div className="flex flex-col min-h-40 max-h-[32rem] overflow-y-auto py-1 pr-3">
          {entries.length === 0 && (
            <div className="flex items-center justify-center flex-grow text-sm text-gray-500">
              <T _str="no projects granted yet" swc />
            </div>
          )}
          {entries.map((entry) => (
            <LedgerRow key={entry.id} entry={entry} showExpiry onCreateInvoice={onCreateInvoice} creatingInvoice={creatingEntryId === entry.id} onDownloadInvoice={downloadInvoice} downloading={downloadingEntryId === entry.id} />
          ))}
          {page?.hasMore && (
            <div className="flex justify-center py-2">
              <Button variant="tertiary" size="xs" onClick={onLoadMore} loading={loading}>
                <T _str="load more" swc />
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal.Container>
  );
};

export const HistoryModal = memo(HistoryModal_);
